import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./OnlineCourses.module.css";

export default function OnlineCourses({ isTeacher = false }) {
  const { t, i18n } = useTranslation("global");
  const [courses, setCourses] = useState([]);
  const [joinedCourses, setJoinedCourses] = useState([]);
  const [teacherCourses, setTeacherCourses] = useState([]);
  const navigate = useNavigate();

  const userData = JSON.parse(localStorage.getItem("userDataLogin"));
  const token = userData?.token;
  const lang = localStorage.getItem("lang")?.replace(/"/g, "") || "en";

  useEffect(() => {
    if (isTeacher) {
      fetchTeacherCourses();
    } else {
      fetchCourses();
      if (token && userData?.user?.type !== "student") {
        fetchJoinedCourses();
      }
    }
  }, [i18n.language, isTeacher]);

  const fetchTeacherCourses = async () => {
    if (!token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/instructor-courses`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": i18n.language,
          },
        }
      );

      const result = await response.json();

      if (response.ok && result.data?.data) {
        setTeacherCourses(result.data.data);
      } else {
        console.error("Error fetching teacher courses:", result);
      }
    } catch (error) {
      console.error("Error fetching teacher courses:", error);
    }
  };

  const fetchCourses = async () => {
    const endpoint = token
      ? `${process.env.REACT_APP_API_URL}/courses`
      : `${process.env.REACT_APP_API_URL}/random_course`;

    try {
      const response = await fetch(endpoint, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
          "Accept-Language": i18n.language,
        },
      });

      const result = await response.json();
      
      if (response.ok) {
        setCourses(result?.data || []);
      } else {
        console.error("Error fetching courses:", result);
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
    }
  };

  const fetchJoinedCourses = async () => {
    if (userData?.user?.type === "student") return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/joint_course`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": lang,
        },
      });

      const result = await response.json();
      
      if (response.ok) {
        setJoinedCourses(result?.data || []);
      } else {
        console.error("Error fetching joined courses:", result);
      }
    } catch (error) {
      console.error("Error fetching joined courses:", error);
    }
  };

  const handleCardClick = async (course) => {
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/payment_course/${course.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (course.type === "free") {
        if (result?.code === 200 || result?.status === 200) {
          navigate(`/CourseDetails/${course.id}`);
          toast.success(result.message || "تم عرض الكورس المجاني بنجاح");
        } else {
          toast.error(result.message || "فشل عرض الكورس المجاني");
        }
      } else if (course.type === "paid") {
        if (course.purshed === 1) {
          navigate(`/CourseDetails/${course.id}`);
          toast.success("تم عرض الكورس المدفوع بنجاح");
        } else {
          if (result?.redirect_url) {
            window.location.href = result.redirect_url;
          } else {
            console.error("Redirect URL Missing:", result);
            toast.error(result.message || "فشل بدء الدفع للكورس المدفوع");
          }
        }
      }
    } catch (error) {
      toast.error("حدث خطأ أثناء محاولة الاشتراك في الكورس");
      console.error("Error:", error);
    }
  };

  const handleTeacherCourseClick = (course) => {
    navigate(`/CourseDetails/${course.id}`);
  };

  const renderCourseCard = (course, index) => {
    let courseImage = "";
    try {
      const images = JSON.parse(course.image);
      courseImage = images[0];
    } catch {
      courseImage = course.image || "assets/imgs/course-1.jpg.svg";
    }

    const instructor = course.instructors?.[0];

    return (
      <div
        key={course.id || index}
        className="col-lg-4 col-md-6 col-12 mb-4"
        style={{ cursor: "pointer" }}
        onClick={() => handleCardClick(course)}
      >
        <div className={styles.courseCard}>
          <div className={styles.courseImageContainer}>
            <img src={courseImage} className={styles.courseImage} alt={course.name} />
          </div>
          <div className={styles.courseCardBody}>
            <h5 className={styles.courseTitle}>{course.name}</h5>
            <p className={styles.courseDescription}>
              {course.stage && course.grade ? (
                `${course.stage} - ${course.grade}`
              ) : course.stage ? (
                course.stage
              ) : course.grade ? (
                course.grade
              ) : (
                t("onlineCourses.noStage") || "مرحلة غير محددة"
              )}
            </p>
            <div className={styles.courseMeta}>
              <div className={styles.courseInstructor}>
                {instructor ? (
                  <>
                    <img
                      src={instructor.image}
                      alt={instructor.name}
                      className="rounded-circle"
                      style={{
                        width: "40px",
                        height: "40px",
                        objectFit: "cover",
                      }}
                    />
                    <span>{instructor.name}</span>
                  </>
                ) : (
                  <div className={styles.noInstructor}>
                    <i className="bi bi-person-circle"></i>
                    <span>{t("onlineCourses.noInstructor") || "مدرب غير محدد"}</span>
                  </div>
                )}
              </div>
              <div className={styles.courseDuration}>
                <i className="bi bi-journal-text"></i>
                {course.lessons} {t("onlineCourses2.lessons")}
              </div>
            </div>
            <div className={styles.coursePrice}>
              {course.type === "free" ? (
                <span className={styles.freePrice}>{t("onlineCourses2.free")}</span>
              ) : (
                <>
                  <span className="currency">$</span>
                  {parseFloat(course.price).toFixed(2)}
                </>
              )}
            </div>
            <button className={styles.enrollButton}>
              {course.type === "free"
                ? t('join')
                : course.purshed === 1
                  ? t('join')
                  : t('subscribe')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderTeacherCourseCard = (course, index) => {
    let courseImage = "";
    try {
      const images = JSON.parse(course.image);
      courseImage = images[0];
    } catch {
      courseImage = course.image || "assets/imgs/course-1.jpg.svg";
    }

    const instructor = course.instructors?.[0];

    return (
      <div
        key={course.id || index}
        className="col-lg-4 col-md-6 col-12 mb-4"
        style={{ cursor: "pointer" }}
        onClick={() => handleTeacherCourseClick(course)}
      >
        <div className={styles.courseCard}>
          <div className={styles.courseImageContainer}>
            <img src={courseImage} className={styles.courseImage} alt={course.name} />
          </div>
          <div className={styles.courseCardBody}>
            <h5 className={styles.courseTitle}>{course.name}</h5>
            <p className={styles.courseDescription}>
              {course.stage && course.grade ? (
                `${course.stage} - ${course.grade}`
              ) : course.stage ? (
                course.stage
              ) : course.grade ? (
                course.grade
              ) : (
                t("onlineCourses.noStage") || "مرحلة غير محددة"
              )}
            </p>
            {course.description && (
              <p className={styles.courseDescription} style={{ fontSize: "14px" }}>
                {course.description}
              </p>
            )}
            <div className={styles.courseMeta}>
              <div className={styles.courseInstructor}>
                {instructor ? (
                  <>
                    <img
                      src={instructor.image}
                      alt={instructor.name}
                      className="rounded-circle"
                      style={{
                        width: "40px",
                        height: "40px",
                        objectFit: "cover",
                      }}
                    />
                    <span>{instructor.name}</span>
                  </>
                ) : (
                  <div className={styles.noInstructor}>
                    <i className="bi bi-person-circle"></i>
                    <span>{t("onlineCourses.noInstructor") || "مدرب غير محدد"}</span>
                  </div>
                )}
              </div>
              <div className={styles.courseDuration}>
                <i className="bi bi-journal-text"></i>
                {course.lessons} {t("onlineCourses2.lessons")}
              </div>
            </div>
            <div className={styles.coursePrice}>
              {course.type === "free" ? (
                <span className={styles.freePrice}>{t("onlineCourses2.free")}</span>
              ) : (
                <>
                  <span className="currency">$</span>
                  {parseFloat(course.price).toFixed(2)}
                </>
              )}
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <span className={`badge ${course.type === 'free' ? 'bg-success' : 'bg-primary'}`}>
                {course.type === 'free' ? t("free") : `$${parseFloat(course.price).toFixed(2)}`}
              </span>
              <span className="badge bg-secondary">
                {course.lessons} {t("onlineCourses2.lessons")}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // If teacher, show teacher courses
  if (isTeacher) {
    return (
      <section className={styles.onlineCoursesSection}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <h1 className={styles.sectionTitle}>{t("profile.myCourses") || "My Courses"}</h1>
            <p className={styles.sectionDescription}>{t("onlineCourses.description")}</p>
          </div>

          <div className={styles.coursesContainer}>
            <div className={`row ${styles.coursesRow}`}>
              {teacherCourses.length > 0 ? (
                teacherCourses.slice(0, 6).map((course, index) => renderTeacherCourseCard(course, index))
              ) : (
                <div className="col-12 text-center">
                  <p className="paragraph-color">{t("profile.noCourses") || "No courses found."}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Regular courses display
  return (
    <section className={styles.onlineCoursesSection}>
      <div className="container">
        <div className={styles.sectionHeader}>
          <h1 className={styles.sectionTitle}>
            {t("onlineCourses.titleOne")}
            <br />
            {t("onlineCourses.titleTwo")}
          </h1>
          <p className={styles.sectionDescription}>{t("onlineCourses.description")}</p>
          <Link to="/courses" className={styles.viewAllLink}>
            {t("onlineCourses.viewAll") || "عرض الكل"}
            {lang === "ar" ? (
              <i className="bi bi-arrow-left"></i>
            ) : (
              <i className="bi bi-arrow-right"></i>
            )}
          </Link>
        </div>

        <div className={styles.coursesContainer}>
          <div className={`row ${styles.coursesRow}`}>
            {(joinedCourses.length > 0 ? joinedCourses.slice(0, 3) : courses.slice(0, 3)).map(
              (course, index) => renderCourseCard(course, index)
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
